"use client";

import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Users, TrendingUp, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import type { TierProgress, CurrentTierInfo } from "@/lib/pricing/types";

type TierProgressBarProps = {
  progress: TierProgress;
  currentTier: CurrentTierInfo;
  variant?: "default" | "compact" | "detailed";
  className?: string;
};

export function TierProgressBar({
  progress,
  currentTier,
  variant = "default",
  className,
}: TierProgressBarProps) {
  const {
    displayedSignups,
    spotsRemainingInTier,
    percentFilled,
    nextTier,
    isLastTier,
  } = progress;

  if (variant === "compact") {
    return (
      <div className={cn("space-y-2", className)}>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            {displayedSignups}/
            {isLastTier ? "ilimitado" : progress.currentTier.cumulativeLimit}{" "}
            miembros
          </span>
          <Badge variant="secondary" className="text-xs">
            {currentTier.annualPriceFormatted}/año
          </Badge>
        </div>
        <Progress value={percentFilled} className="h-2" />
      </div>
    );
  }

  if (variant === "detailed") {
    return (
      <div className={cn("space-y-4", className)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="text-primary h-5 w-5" />
            <span className="font-semibold">Precio de Miembro Fundador</span>
          </div>
          <Badge className="bg-brand-red/20 text-brand-red border-brand-red/30">
            Tier {currentTier.tier.tierNumber}
          </Badge>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Lugares ocupados</span>
            <span className="font-medium">
              {displayedSignups} /{" "}
              {isLastTier ? "ilimitado" : progress.currentTier.cumulativeLimit}
            </span>
          </div>
          <Progress value={percentFilled} className="h-3" />
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="bg-muted/50 flex items-center gap-2 rounded-lg p-3">
            <Clock className="text-muted-foreground h-4 w-4" />
            <div>
              <p className="text-muted-foreground text-xs">Lugares restantes</p>
              <p className="font-semibold">
                {isLastTier ? "Ilimitados" : spotsRemainingInTier}
              </p>
            </div>
          </div>
          <div className="bg-muted/50 flex items-center gap-2 rounded-lg p-3">
            <TrendingUp className="text-muted-foreground h-4 w-4" />
            <div>
              <p className="text-muted-foreground text-xs">
                Próximo precio anual
              </p>
              <p className="font-semibold">
                {nextTier
                  ? `$${(nextTier.annualPriceCents / 100).toFixed(2)}/año`
                  : "Precio máximo"}
              </p>
            </div>
          </div>
        </div>

        {!isLastTier && spotsRemainingInTier <= 10 && (
          <div className="bg-brand-red/10 text-brand-red rounded-lg p-3 text-center text-sm font-medium">
            ¡Solo quedan {spotsRemainingInTier} lugares a este precio!
          </div>
        )}
      </div>
    );
  }

  // Default variant
  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="text-muted-foreground h-4 w-4" />
          <span className="text-sm font-medium">Miembros fundadores</span>
        </div>
        {!isLastTier && spotsRemainingInTier <= 20 && (
          <Badge variant="destructive" className="text-xs">
            ¡Quedan {spotsRemainingInTier}!
          </Badge>
        )}
      </div>

      <Progress value={percentFilled} className="h-2.5" />

      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">
          {displayedSignups} de{" "}
          {isLastTier ? "ilimitado" : progress.currentTier.cumulativeLimit}{" "}
          lugares
        </span>
        {nextTier && (
          <span className="text-muted-foreground">
            Próximo: ${(nextTier.annualPriceCents / 100).toFixed(2)}/año
          </span>
        )}
      </div>
    </div>
  );
}
